import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
// Dynamic import to avoid circular dependency initialization issues
// import { CampaignPerformanceCache } from '@/lib/campaign-performance-cache'

interface CampaignPerformanceData {
  leadsContacted: number
  sendVolume: number
  replies: number
  humanReplies: number
  positiveReplies: number
  replyRate: number
  humanReplyRate: number
  positiveRate: number
  meetingsBooked: number
  dealsWon: number
  revenueGenerated: number
  loading?: boolean
  lastUpdated?: string
}

interface SeparatedCampaignPerformanceData {
  email: CampaignPerformanceData
  linkedin: CampaignPerformanceData
  hasLinkedIn: boolean
}

/**
 * Hook for fetching separated campaign performance data (email vs linkedin)
 */
export function useSeparatedCampaignPerformance(campaignId: string | undefined) {
  const [data, setData] = useState<SeparatedCampaignPerformanceData>({
    email: {
      leadsContacted: 0,
      sendVolume: 0,
      replies: 0,
      humanReplies: 0,
      positiveReplies: 0,
      replyRate: 0,
      humanReplyRate: 0,
      positiveRate: 0,
      meetingsBooked: 0,
      dealsWon: 0,
      revenueGenerated: 0
    },
    linkedin: {
      leadsContacted: 0,
      sendVolume: 0,
      replies: 0,
      humanReplies: 0,
      positiveReplies: 0,
      replyRate: 0,
      humanReplyRate: 0,
      positiveRate: 0,
      meetingsBooked: 0,
      dealsWon: 0,
      revenueGenerated: 0
    },
    hasLinkedIn: false
  })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!campaignId) return

    let isMounted = true

    const loadData = async () => {
      try {
        setLoading(true)
        const { UnifiedCampaignPerformance } = await import('@/lib/unified-campaign-performance')
        const separatedPerformance = await UnifiedCampaignPerformance.getSeparatedCampaignPerformance(campaignId)
        
        if (isMounted) {
          setData(separatedPerformance)
          setLoading(false)
        }
      } catch (error) {
        console.error('❌ Error fetching separated campaign performance:', error)
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [campaignId])

  return { data, loading }
}

/**
 * Hook for fetching multiple campaigns with separated performance data
 */
export function useCachedMultipleCampaignsSeparatedPerformance(campaigns: any[]) {
  const [performanceData, setPerformanceData] = useState<Record<string, SeparatedCampaignPerformanceData>>({})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!campaigns.length) {
      setPerformanceData({})
      setLoading(false)
      return
    }

    let isMounted = true

    const loadData = async () => {
      try {
        setLoading(true)
        const { UnifiedCampaignPerformance } = await import('@/lib/unified-campaign-performance')
        
        const results: Record<string, SeparatedCampaignPerformanceData> = {}
        
        // Fetch separated performance for each campaign
        const promises = campaigns.map(async (campaign) => {
          try {
            const separatedPerformance = await UnifiedCampaignPerformance.getSeparatedCampaignPerformance(campaign.id)
            results[campaign.id] = separatedPerformance
          } catch (error) {
            console.error(`❌ Error fetching separated performance for campaign ${campaign.id}:`, error)
            results[campaign.id] = {
              email: {
                leadsContacted: 0,
                sendVolume: 0,
                replies: 0,
                humanReplies: 0,
                positiveReplies: 0,
                replyRate: 0,
                humanReplyRate: 0,
                positiveRate: 0,
                meetingsBooked: 0,
                dealsWon: 0,
                revenueGenerated: 0
              },
              linkedin: {
                leadsContacted: 0,
                sendVolume: 0,
                replies: 0,
                humanReplies: 0,
                positiveReplies: 0,
                replyRate: 0,
                humanReplyRate: 0,
                positiveRate: 0,
                meetingsBooked: 0,
                dealsWon: 0,
                revenueGenerated: 0
              },
              hasLinkedIn: false
            }
          }
        })

        await Promise.all(promises)

        if (isMounted) {
          setPerformanceData(results)
          setLoading(false)
        }
      } catch (error) {
        console.error('❌ Error loading separated campaign performance data:', error)
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [campaigns])

  return { performanceData, loading }
}

/**
 * Single campaign performance hook with proper caching
 */
export function useCachedCampaignPerformance(campaignId: string | undefined, campaignName?: string) {
  const [data, setData] = useState<CampaignPerformanceData>({
    leadsContacted: 0,
    sendVolume: 0,
    replies: 0,
    humanReplies: 0,
    positiveReplies: 0,
    replyRate: 0,
    positiveRate: 0,
    meetingsBooked: 0,
    dealsWon: 0,
    revenueGenerated: 0,
    loading: true
  })

  useEffect(() => {
    if (!campaignId) return

    let isMounted = true

    const loadData = async () => {
      try {
        // Use dynamic import to avoid circular dependency initialization issues
        const { CampaignPerformanceCache } = await import('@/lib/campaign-performance-cache')
        const performance = await CampaignPerformanceCache.getCampaignPerformance(campaignId, campaignName)
        
        if (isMounted) {
          setData({
            ...performance,
            loading: false
          })
        }
      } catch (error) {
        console.error(`Error loading performance for ${campaignId}:`, error)
        if (isMounted) {
          setData(prev => ({ ...prev, loading: false }))
        }
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [campaignId, campaignName])

  return data
}

/**
 * Optimized multiple campaigns performance hook
 */
export function useCachedMultipleCampaignsPerformance(campaigns: Array<{id: string, name: string, status: string, client_id: string}>) {
  const [performanceData, setPerformanceData] = useState<Record<string, CampaignPerformanceData>>({})
  const [loading, setLoading] = useState(true)
  const [lastFetchTime, setLastFetchTime] = useState(0)
  const [isInitialized, setIsInitialized] = useState(false)

  // FIXED: Memoize campaign IDs with stable reference to prevent unnecessary re-renders
  // Create stable string of campaign IDs that only changes when IDs actually change
  const campaignIdsString = useMemo(() => {
    return campaigns.map(c => c.id).sort().join(',')
  }, [campaigns.map(c => c.id).sort().join(',')])
  
  const campaignIds = useMemo(() => campaignIdsString, [campaignIdsString])
  const campaignMap = useMemo(() => new Map(campaigns.map(c => [c.id, c])), [campaignIdsString])

  // Load cached data immediately on first render
  useEffect(() => {
    if (!isInitialized && campaigns.length > 0) {
      console.log('🔍 Loading cached campaign data immediately...')
      
      const loadCachedData = async () => {
        // Use dynamic import to avoid circular dependency initialization issues
        const { CampaignPerformanceCache } = await import('@/lib/campaign-performance-cache')
        const cachedData: Record<string, CampaignPerformanceData> = {}
        
        // Try to get cached data for each campaign
        for (const campaign of campaigns) {
          try {
            const cachedItem = CampaignPerformanceCache.getFromLocalStorage(campaign.id, true)
            if (cachedItem) {
              cachedData[campaign.id] = {
                ...cachedItem,
                loading: false
              }
              console.log(`📦 Found cached data for campaign ${campaign.id}`)
            }
          } catch (error) {
            console.error(`Failed to load cached data for ${campaign.id}:`, error)
          }
        }
        
        // If we have any cached data, display it immediately
        if (Object.keys(cachedData).length > 0) {
          setPerformanceData(cachedData)
          console.log(`✅ Loaded ${Object.keys(cachedData).length} cached campaigns`)
        }
        
        setIsInitialized(true)
      }
      
      loadCachedData()
    }
  }, [campaigns, isInitialized])

  const loadCampaignData = useCallback(async (campaign: {id: string, name: string}) => {
    try {
      // Use dynamic import to avoid circular dependency initialization issues
      const { CampaignPerformanceCache } = await import('@/lib/campaign-performance-cache')
      const performance = await CampaignPerformanceCache.getCampaignPerformance(campaign.id, campaign.name)
      return {
        [campaign.id]: {
          ...performance,
          loading: false
        }
      }
    } catch (error) {
      console.error(`Error loading performance for ${campaign.id}:`, error)
      return {
        [campaign.id]: {
          leadsContacted: 0,
          sendVolume: 0,
          replies: 0,
          humanReplies: 0,
          positiveReplies: 0,
          replyRate: 0,
          positiveRate: 0,
          meetingsBooked: 0,
          dealsWon: 0,
          revenueGenerated: 0,
          loading: false
        }
      }
    }
  }, [])

  // FIXED: Memoize loadAllData with stable dependencies to prevent recreation
  const loadAllData = useCallback(async (forceRefresh = false) => {
    // Get campaigns from the memoized map (stable reference)
    const currentCampaigns = Array.from(campaignMap.values())
    if (currentCampaigns.length === 0) {
      setLoading(false)
      return
    }

    // Check for demo mode first
    const { smartleadAPI } = await import('@/lib/smartlead-api')
    if (!smartleadAPI.isConfigured()) {
      console.log('🔑 [useCachedMultipleCampaignsPerformance] Smartlead API not configured, returning demo performance data...')
      
      // Import demo data
      const { getDemoCampaignById } = await import('@/data/demoData')
      
      const demoPerformanceData: Record<string, CampaignPerformanceData> = {}
      
      currentCampaigns.forEach(campaign => {
        console.log(`🔍 Looking up demo campaign for ID: "${campaign.id}" (type: ${typeof campaign.id})`)
        const demoCampaign = getDemoCampaignById(campaign.id)
        console.log(`🔍 Demo campaign found:`, !!demoCampaign, demoCampaign ? `ID: ${demoCampaign.id}` : 'Not found')
        
        if (demoCampaign) {
          demoPerformanceData[campaign.id] = {
            leadsContacted: demoCampaign.total_leads,
            sendVolume: demoCampaign.emails_sent, // Fixed: was sent_count
            replies: demoCampaign.replies_received, // Fixed: was reply_count
            humanReplies: demoCampaign.replies_received, // Fixed: was reply_count
            positiveReplies: demoCampaign.positive_replies, // Fixed: was positive_reply_count
            replyRate: demoCampaign.reply_rate,
            positiveRate: demoCampaign.positive_reply_rate,
            meetingsBooked: demoCampaign.meetings_booked,
            dealsWon: demoCampaign.deals_closed, // Fixed: was deals_won
            revenueGenerated: demoCampaign.deals_closed * 15000, // Calculate revenue from deals
            loading: false
          }
          console.log(`📊 Demo performance for campaign ${campaign.id} (${campaign.name}):`, {
            total_leads: demoCampaign.total_leads,
            emails_sent: demoCampaign.emails_sent,
            replies_received: demoCampaign.replies_received,
            positive_replies: demoCampaign.positive_replies,
            meetings_booked: demoCampaign.meetings_booked,
            deals_closed: demoCampaign.deals_closed,
            revenue_calculated: demoCampaign.deals_closed * 15000
          })
        } else {
          // Fallback for campaigns not in demo data
          demoPerformanceData[campaign.id] = {
            leadsContacted: 0,
            sendVolume: 0,
            replies: 0,
            humanReplies: 0,
            positiveReplies: 0,
            replyRate: 0,
            positiveRate: 0,
            meetingsBooked: 0,
            dealsWon: 0,
            revenueGenerated: 0,
            loading: false
          }
        }
      })
      
      setPerformanceData(demoPerformanceData)
      setLoading(false)
      console.log(`📊 Demo: Loaded performance data for ${Object.keys(demoPerformanceData).length} campaigns`)
      return
    }

    const now = Date.now()
    // Prevent rapid successive fetches unless forced
    if (!forceRefresh && now - lastFetchTime < 1000) {
      return
    }

    setLastFetchTime(now)
    
    // Only show loading if we don't have any data yet
    if (Object.keys(performanceData).length === 0) {
      setLoading(true)
    }

    try {
      // Use dynamic import to avoid circular dependency initialization issues
      const { CampaignPerformanceCache } = await import('@/lib/campaign-performance-cache')
      // Use the new bulk method for much faster loading
      const campaignIdsArray = currentCampaigns.map(c => c.id)
      const bulkResults = await CampaignPerformanceCache.getMultipleCampaignsPerformance(campaignIdsArray)
      
      // Convert bulk results to the expected format
      const allData: Record<string, CampaignPerformanceData> = {}
      for (const [campaignId, performance] of Object.entries(bulkResults)) {
        allData[campaignId] = {
          ...performance,
          loading: false
        }
      }

      // Only update if we got meaningful data (not all zeros)
      const hasNonZeroData = Object.values(allData).some(data => 
        data.leadsContacted > 0 || data.sendVolume > 0 || data.replies > 0 || data.meetingsBooked > 0
      )
      
      if (hasNonZeroData || Object.keys(performanceData).length === 0) {
        setPerformanceData(allData)
        console.log(`📊 Updated performance data for ${Object.keys(allData).length} campaigns`)
      } else {
        console.log('⚠️ Skipping update with all-zero data, keeping existing data')
      }
    } catch (error) {
      console.error('Error loading campaign performance data:', error)
      // Don't clear existing data on error
    } finally {
      setLoading(false)
    }
  }, [campaignIdsString, campaignMap, lastFetchTime]) // FIXED: Use campaignIdsString and campaignMap for stable dependencies

  // FIXED: Only trigger reload when campaign IDs actually change and we're initialized
  // Use loadAllData in dependencies but it's now stable due to useCallback with campaignIdsString
  useEffect(() => {
    if (campaignIdsString && isInitialized && campaigns.length > 0) {
      // Campaign IDs changed, loading fresh data
      loadAllData()
    }
  }, [campaignIdsString, isInitialized, loadAllData, campaigns.length])

  // DISABLED: Listen for cache updates - CAUSING INFINITE LOOPS
  useEffect(() => {
    // Cache update listener disabled to prevent infinite loops
    
    // DISABLED: All automatic cache update listeners
    // Data will be loaded on demand only
    
    return () => {
      // Clean up any existing listeners
    }
  }, []) // Empty dependency array - no automatic refresh

  // Refresh function for manual triggers - now preserves cached data
  const refresh = useCallback(() => {
    // Manual refresh triggered
    // Don't clear cache completely, just force a fresh fetch
    setLastFetchTime(0)
    loadAllData(true) // Force refresh without clearing cache
  }, [loadAllData])

  // FIXED: Reduced logging - only log when data changes or when debugging is needed
  // Removed console.log that runs on every render to reduce console spam
  // Uncomment below for debugging:
  // console.log(`🔍 [useCachedMultipleCampaignsPerformance] Returning:`, {
  //   performanceDataKeys: Object.keys(performanceData),
  //   performanceDataCount: Object.keys(performanceData).length,
  //   loading,
  //   sampleData: Object.keys(performanceData).length > 0 ? performanceData[Object.keys(performanceData)[0]] : 'No data'
  // })
  
  return { 
    performanceData, 
    loading,
    refresh
  }
}